/**
 * MC_SUPPLIER_REASSIGN: change the supplier an automatic match bound.
 *
 * The staging match is name-based and can bind the wrong supplier. Studio V2
 * only offered the picker while NO supplier was bound, so a wrong binding was
 * a dead end: the article browser serves the bound supplier's pool, and the
 * reviewer could not reach the right articles.
 *
 * This is `SupplierAssignControl` (search, inline create, assign) behind two
 * additions, supplied through its `beforeAssign` veto point rather than a
 * fork of it:
 *
 *   - Picking the supplier that is ALREADY bound does nothing. The PUT must
 *     not fire: it writes a compliance event and teaches Turnus Memory that
 *     this printed name means the supplier it is bound to, which here would
 *     record the wrong match as a human decision.
 *   - Picking a different one asks first, because `onAssigned` is wired by
 *     the caller to the mandatory gate's chain (assign, then `/reextract`
 *     behind the recompute latch). That discards and re-stages the
 *     declaration against the new supplier's articles, which is not a
 *     gesture to fire on a misclick.
 */
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { ArrowRight } from "lucide-react"
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react"
import { SupplierAssignControl } from "./SupplierAssignControl"

export interface SupplierReassignControlProps {
  declId: string
  /** The supplier bound right now (`useStudioV2State`'s `supplierId`). */
  currentSupplierId: string | null
  /** Its name, shown as the "from" side of the confirmation. */
  currentSupplierName?: string | null
  supplierMentioned: string | null
  /** Prefill fallback for the create form, as on the unmatched chip. */
  supplierName?: string | null
  trigger: ReactNode
  /** Fires after a successful assignment of a DIFFERENT supplier. */
  onAssigned: (supplierId: string) => void
}

export function SupplierReassignControl({
  declId,
  currentSupplierId,
  currentSupplierName,
  supplierMentioned,
  supplierName,
  trigger,
  onAssigned,
}: SupplierReassignControlProps) {
  const { translate } = useTranslation()
  // The pick the open confirmation is about. `name` is empty when it is not
  // known (the number-conflict "assign instead" path only has an id).
  const [pending, setPending] = useState<{ name: string } | null>(null)
  const settleRef = useRef<((ok: boolean) => void) | null>(null)

  const settle = useCallback((ok: boolean) => {
    const resolve = settleRef.current
    settleRef.current = null
    setPending(null)
    resolve?.(ok)
  }, [])

  const beforeAssign = useCallback(
    (supplier: { id: string | null; name: string }): Promise<boolean> => {
      if (supplier.id !== null && supplier.id === currentSupplierId) {
        return Promise.resolve(false)
      }
      // A confirmation still open from an earlier pick is declined, never
      // left dangling.
      settleRef.current?.(false)
      return new Promise<boolean>((resolve) => {
        settleRef.current = resolve
        setPending({ name: supplier.name })
      })
    },
    [currentSupplierId],
  )

  // Unmounting with a question open (the declaration switched underneath the
  // reviewer) declines it.
  useEffect(
    () => () => {
      settleRef.current?.(false)
      settleRef.current = null
    },
    [],
  )

  return (
    <>
      <SupplierAssignControl
        declId={declId}
        supplierMentioned={supplierMentioned}
        supplierName={supplierName}
        trigger={trigger}
        beforeAssign={beforeAssign}
        onAssigned={onAssigned}
      />
      {/* An AlertDialog, not a Dialog: `useStudioV2Keyboard` stands down for
          `role="alertdialog"` only (every Radix popover wears `role="dialog"`
          and stays non-blocking), so with a plain Dialog Esc would also leave
          for the list and 1/2/3 would approve or defer underneath it. */}
      <AlertDialog
        open={pending !== null}
        onOpenChange={(next) => {
          if (!next) settle(false)
        }}
      >
        <AlertDialogContent
          className="max-w-sm"
          onEscapeKeyDown={(e) => e.stopPropagation()}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>
              {translate("MC_DECL_V2_SUP_CHANGE_TITLE")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {translate("MC_DECL_V2_SUP_CHANGE_BODY")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {pending?.name ? (
            <div className="flex items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-[13px]">
              {currentSupplierName ? (
                <>
                  <span className="truncate text-muted-foreground">
                    {currentSupplierName}
                  </span>
                  <ArrowRight
                    className="size-3.5 shrink-0 text-muted-foreground"
                    aria-hidden
                  />
                </>
              ) : null}
              <span className="truncate font-medium">{pending.name}</span>
            </div>
          ) : null}
          <p className="text-xs text-muted-foreground">
            {translate("MC_DECL_V2_SUP_CHANGE_HINT")}
          </p>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {translate("MC_DECL_V2_SUP_CHANGE_CANCEL")}
            </AlertDialogCancel>
            <Button
              type="button"
              className="bg-attention text-black hover:bg-attention-strong"
              onClick={() => settle(true)}
            >
              {translate("MC_DECL_V2_SUP_CHANGE_CONFIRM")}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
